import axios from 'axios';
import { API_CONFIG } from '../config/api.js';

/**
 * Places Service
 * Handles Google Places autocomplete, details, and geocoding API calls
 * Uses a separate axios instance with authentication but graceful error handling
 */

// Create a separate axios instance for places API with graceful error handling
const placesClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include both client type and auth headers
placesClient.interceptors.request.use(
  (config) => {
    // Add client type header
    config.headers[API_CONFIG.HEADERS.CLIENT_TYPE] = 'web';

    // Add JWT token if available for places API
    const token = localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor that handles errors gracefully without redirect
placesClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error but do NOT redirect to login for places API errors
    console.warn('Places API error:', error.response?.status, error.message);

    // For places API, we want to gracefully handle auth errors without redirecting
    if (error.response?.status === 401) {
      console.warn('Places API: Authentication failed, but continuing without redirect');
      // Return a structured error that components can handle
      return Promise.reject({
        response: {
          data: {
            success: false,
            message: 'Authentication required for places API',
            data: []
          }
        }
      });
    }

    return Promise.reject(error);
  }
);

const placesService = {
  /**
   * Get place autocomplete suggestions as user types
   * @param {string} input - Search text (min 2 chars)
   * @param {Object} options - Additional options (country, lat, lng, radius)
   * @returns {Promise} Array of place suggestions
   */
  async getAutocomplete(input, options = {}) {
    try {
      const params = new URLSearchParams({ input });

      if (options.country) params.append('country', options.country);
      if (options.lat) params.append('lat', options.lat);
      if (options.lng) params.append('lng', options.lng);
      if (options.radius) params.append('radius', options.radius);

      const response = await placesClient.get(`/places/autocomplete?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching place autocomplete:', error);

      // Return empty results for auth errors to allow graceful fallback
      if (error.response?.data?.message === 'Authentication required for places API') {
        return {
          success: false,
          message: 'Authentication required for places search',
          data: []
        };
      }

      throw error;
    }
  },

  /**
   * Get detailed place information including coordinates
   * @param {string} placeId - Google Place ID
   * @returns {Promise} Place details with coordinates
   */
  async getPlaceDetails(placeId) {
    try {
      const response = await placesClient.get(`/places/details/${placeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching place details:', error);

      // Return null for auth errors to allow graceful fallback
      if (error.response?.data?.message === 'Authentication required for places API') {
        return {
          success: false,
          message: 'Authentication required for place details',
          data: null
        };
      }

      throw error;
    }
  },

  /**
   * Geocode an address to get coordinates
   * @param {string} address - Full address string
   * @returns {Promise} Location with coordinates
   */
  async geocodeAddress(address) {
    try {
      const params = new URLSearchParams({ address });
      const response = await placesClient.get(`/places/geocode?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error geocoding address:', error);

      // Return null for auth errors to allow graceful fallback
      if (error.response?.data?.message === 'Authentication required for places API') {
        return {
          success: false,
          message: 'Authentication required for geocoding',
          data: null
        };
      }

      throw error;
    }
  },

  /**
   * Reverse geocode coordinates to get address
   * @param {number} latitude - Latitude coordinate
   * @param {number} longitude - Longitude coordinate
   * @returns {Promise} Address information
   */
  async reverseGeocode(latitude, longitude) {
    try {
      const params = new URLSearchParams({
        lat: latitude.toString(),
        lng: longitude.toString()
      });
      const response = await placesClient.get(`/places/reverse-geocode?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error reverse geocoding:', error);

      // Return null for auth errors to allow graceful fallback
      if (error.response?.data?.message === 'Authentication required for places API') {
        return {
          success: false,
          message: 'Authentication required for reverse geocoding',
          data: null
        };
      }

      // Return failure for any error - caller should handle fallback
      return {
        success: false,
        message: error.message || 'Reverse geocoding failed',
        data: null
      };
    }
  }
};

export default placesService;
